import React, { useState } from "react";
import { Button, Modal, Spin, Tooltip } from "antd";
import { useSelector } from "react-redux";

import * as projectsApi from "../../../../core/data/redux/services/projectsApi";
import { selectActiveCompany } from "../../../../core/data/redux/projects/selectors";
import { SETTINGS_MODAL_PROPS } from "../../helpers/modal.style";
import { errMsg } from "../../helpers/errMsg.helper";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import { notify } from "../../../../services/toast.config";

import type { Activity } from "../../../../core/data/redux/projects/types";

interface ActivityFeedProps {
  activity: Activity[];
  emptyMessage?: string;
  initialLimit?: number;
  viewAll?: {
    projectId: number;
    entityType?: string;
    entityId?: number;
    title?: string;
  };
}

const ACTION_ICONS: Record<
  string,
  { icon: string; color: string; bg: string }
> = {
  created: { icon: "ti ti-plus", color: "#10b981", bg: "#ecfdf5" },
  updated: { icon: "ti ti-edit", color: "#3b82f6", bg: "#eff6ff" },
  deleted: { icon: "ti ti-trash", color: "#ef4444", bg: "#fef2f2" },
  moved: { icon: "ti ti-arrows-move", color: "#f59e0b", bg: "#fffbeb" },
  status_changed: { icon: "ti ti-refresh", color: "#3b82f6", bg: "#eff6ff" },
  assigned: { icon: "ti ti-user-check", color: "#10b981", bg: "#ecfdf5" },
  commented: { icon: "ti ti-message", color: "#8b5cf6", bg: "#faf5ff" },
  started_sprint: { icon: "ti ti-player-play", color: "#3b82f6", bg: "#eff6ff" },
  completed_sprint: { icon: "ti ti-flag-check", color: "#10b981", bg: "#ecfdf5" },
  added: { icon: "ti ti-user-plus", color: "#3b82f6", bg: "#eff6ff" },
  removed: { icon: "ti ti-user-minus", color: "#ef4444", bg: "#fef2f2" },
  role_changed: { icon: "ti ti-user-edit", color: "#f59e0b", bg: "#fffbeb" },
  watcher_added: { icon: "ti ti-eye", color: "#3b82f6", bg: "#eff6ff" },
  watcher_removed: { icon: "ti ti-eye-off", color: "#64748b", bg: "#f1f5f9" },
  worklog_created: { icon: "ti ti-clock", color: "#0ea5e9", bg: "#e0f2fe" },
};

const ActivityRow: React.FC<{ a: Activity; muted?: boolean }> = ({
  a,
  muted,
}) => {
  const meta = ACTION_ICONS[a.action] || {
    icon: "ti ti-history",
    color: "#64748b",
    bg: "#f1f5f9",
  };
  return (
    <li
      className="d-flex gap-2"
      style={{
        padding: "8px 10px",
        borderRadius: 10,
        background: muted ? "#f8fafc" : "transparent",
        transition: "background 0.2s",
      }}
    >
      <div
        className="rounded-circle d-flex align-items-center justify-content-center"
        style={{
          width: 32,
          height: 32,
          flexShrink: 0,
          background: meta.bg,
        }}
      >
        <i
          className={meta.icon}
          style={{ color: meta.color, fontSize: 14 }}
        />
      </div>
      <div className="flex-grow-1 min-width-0">
        <div style={{ fontSize: 13 }}>
          <strong style={{ color: "#1e293b" }}>
            {a.actor_snapshot?.name || `User #${a.actor_id}`}
          </strong>{" "}
          <span className="text-muted">
            {a.action.replace(/_/g, " ")}
          </span>{" "}
          <span style={{ color: "#475569", fontWeight: 500 }}>
            {a.entity_type.toLowerCase()}
          </span>
        </div>
        <Tooltip title={new Date(a.created_at).toLocaleString()}>
          <span className="text-muted" style={{ fontSize: 11.5 }}>
            {humanReadableDate(a.created_at)}
          </span>
        </Tooltip>
      </div>
    </li>
  );
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activity,
  emptyMessage = "No activity yet.",
  initialLimit = 10,
  viewAll,
}) => {
  const activeCompany = useSelector(selectActiveCompany);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalItems, setModalItems] = useState<Activity[]>([]);
  const [modalPage, setModalPage] = useState(1);
  const [modalHasMore, setModalHasMore] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const loadModalActivities = async (page: number, append = false) => {
    if (!viewAll || !activeCompany?.company_id) return;
    setModalLoading(true);
    try {
      const data = await projectsApi.listProjectActivity(
        activeCompany.company_id,
        activeCompany.company_type,
        viewAll.projectId,
        page,
        20,
        viewAll.entityType,
        viewAll.entityId
      );
      const fetched: Activity[] = data?.activity || [];
      setModalItems((prev) => (append ? [...prev, ...fetched] : fetched));
      setModalHasMore(
        data?.pagination?.page < data?.pagination?.total_pages
      );
      setModalPage(page);
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to load activity"));
    } finally {
      setModalLoading(false);
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
    loadModalActivities(1, false);
  };

  const shown = activity.slice(0, initialLimit);
  const hasMore = viewAll && activity.length >= initialLimit;

  if (activity.length === 0) {
    return <p className="text-muted small mb-0">{emptyMessage}</p>;
  }

  return (
    <>
      <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
        {shown.map((a) => (
          <ActivityRow key={a.id} a={a} />
        ))}
      </ul>
      {hasMore && (
        <div className="text-center mt-3">
          <button
            type="button"
            onClick={handleOpenModal}
            className="btn btn-sm d-inline-flex align-items-center gap-1"
            style={{
              borderRadius: 8,
              background: "#f1f5f9",
              color: "#475569",
              fontSize: 12,
              fontWeight: 500,
              border: "none",
            }}
          >
            <i className="ti ti-list" style={{ fontSize: 14 }} />
            View All
          </button>
        </div>
      )}

      <Modal
        {...SETTINGS_MODAL_PROPS}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        title={
          <div className="d-flex align-items-center gap-2">
            <i
              className="ti ti-history"
              style={{ color: "#8b5cf6", fontSize: 20 }}
            />
            <span>{viewAll?.title || "All Activity"}</span>
          </div>
        }
      >
        <div
          className="d-flex flex-column gap-2"
          style={{
            maxHeight: "65vh",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {modalItems.length === 0 && !modalLoading ? (
            <div className="text-center py-4">
              <i
                className="ti ti-history-off"
                style={{ fontSize: 36, color: "#cbd5e1" }}
              />
              <p
                className="text-muted mb-0 mt-2"
                style={{ fontSize: 13 }}
              >
                No activity found.
              </p>
            </div>
          ) : (
            <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
              {modalItems.map((a) => (
                <ActivityRow key={a.id} a={a} muted />
              ))}
            </ul>
          )}
          {modalLoading && (
            <div className="text-center py-3">
              <Spin />
            </div>
          )}
          {!modalLoading && modalHasMore && modalItems.length > 0 && (
            <div className="text-center mt-2 mb-2">
              <Button
                type="dashed"
                onClick={() => loadModalActivities(modalPage + 1, true)}
                style={{ borderRadius: 8, color: "#475569" }}
              >
                Load More
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ActivityFeed;
